<ng-template #body>
  <ng-content />
</ng-template>

<!--
  사이드바 내부(투영 슬롯 포함)는 여기 한 번만 선언한다 — toolbar/none 두 분기가 각자
  <ng-content select="[shellSidenavHeader]">를 두면, 슬롯 인덱스가 컴파일 타임에 정해지고
  투영 노드가 첫 번째 매칭 슬롯에만 들어가기 때문에 렌더되지 않는 분기 쪽이 콘텐츠를 조용히
  버린다. *ngTemplateOutlet으로 참조하면 이 문제가 없다(#body와 동일한 이유).
-->
<ng-template #sidenavBody>
  @if (brandInSidenav()) {
    <div class="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
      <sbe-shell-brand
        [logo]="resolvedLogo()"
        [logoDark]="resolvedLogoDark()"
        [logoAltText]="resolvedLogoAlt()"
        [label]="resolvedLabel()"
        [homeRoute]="homeRoute()"
      />
    </div>
  }
  <ng-content select="[shellSidenavHeader]" />
  <!--
    항목 렌더링(routerLink <a> / action <button>, icon, trailingIcon)을 템플릿으로 빼서
    평평한 항목과 heading 섹션 안의 들여쓴 항목 양쪽에서 재사용한다(#body와 동일한
    ngTemplateOutlet 관례) — 2026-08-29, ShellNavSection(heading/들여쓰기) 도입.
    routerLink 항목은 <a>, action 항목(팝업으로 외부 서비스 진입 등 라우트가 아닌 클릭
    핸들러)은 <button> — 같은 <ul> 안 <li>로 들어가 목록 자체의 gap-1을 그대로 물려받는다
    (2026-08-28, 사용자 지적 — 예전엔 action 항목을 shellSidenavContent로 목록 밖에 별도
    투영해 목록의 padding/gap과 안 맞아 간격을 매번 보정해야 했다).
  -->
  <ng-template #navItemTpl let-item="item" let-linkClass="linkClass">
    <li class="group relative" [class.cursor-not-allowed]="item.disabled" [attr.title]="item.tooltip || null">
      @if (item.routerLink) {
        <a
          [routerLink]="item.disabled ? null : item.routerLink"
          routerLinkActive="bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400"
          [routerLinkActiveOptions]="{ exact: item.exact ?? false }"
          [class]="linkClass"
          [class.pointer-events-none]="item.disabled"
          [class.opacity-50]="item.disabled"
          [class.!pr-9]="item.secondaryLink"
          (click)="onNavigate()"
        >
          @if (item.icon) {
            <mat-icon class="!h-5 !w-5 !text-xl">{{ item.icon }}</mat-icon>
          }
          <span class="flex-1">{{ item.label }}</span>
          @if (item.trailingIcon) {
            <mat-icon class="!h-4 !w-4 !text-base opacity-50">{{ item.trailingIcon }}</mat-icon>
          }
        </a>
      } @else {
        <button
          type="button"
          [class]="linkClass"
          [disabled]="item.disabled"
          [class.pointer-events-none]="item.disabled"
          [class.opacity-50]="item.disabled"
          [class.!pr-9]="item.secondaryLink"
          (click)="item.action?.(); onNavigate()"
        >
          @if (item.icon) {
            <mat-icon class="!h-5 !w-5 !text-xl">{{ item.icon }}</mat-icon>
          }
          <span class="flex-1">{{ item.label }}</span>
          @if (item.trailingIcon) {
            <mat-icon class="!h-4 !w-4 !text-base opacity-50">{{ item.trailingIcon }}</mat-icon>
          }
        </button>
      }
      @if (item.secondaryLink; as secondary) {
        <!-- 항목 오른쪽에 겹쳐 두는 보조 링크(예: 설정 톱니바퀴). 마우스를 올리거나 키보드 포커스가 오면 보이고, 호버가 없는 터치 기기에서는 항상 보인다. -->
        <a
          [routerLink]="secondary.routerLink"
          [attr.aria-label]="secondary.label"
          [attr.title]="secondary.label"
          class="absolute top-1/2 right-1.5 flex -translate-y-1/2 items-center rounded p-1 text-gray-500 opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 hover:bg-gray-100 focus-visible:opacity-100 dark:text-gray-400 dark:hover:bg-gray-800 [@media(hover:none)]:opacity-100"
          (click)="onNavigate()"
        >
          <mat-icon class="!h-4 !w-4 !text-base">{{ secondary.icon }}</mat-icon>
        </a>
      }
    </li>
  </ng-template>
  <nav aria-label="주요 메뉴" class="p-6">
    <ul class="flex flex-col gap-1">
      @for (entry of navItems(); track $index) {
        @if (isNavSection(entry)) {
          @if (entry.items.length > 0) {
            @if (entry.heading) {
              @if (entry.reorder; as reorder) {
                <li [class]="navHeadingClass + ' flex items-center justify-between gap-1'">
                  <span>{{ entry.heading }}</span>
                  @if (reorder.editing) {
                    <span class="flex items-center gap-1 normal-case">
                      <button type="button" class="rounded px-2 py-0.5 text-xs hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-gray-800" [disabled]="reorder.saving" (click)="reorder.onCancel()">취소</button>
                      <button type="button" class="rounded bg-blue-600 px-2 py-0.5 text-xs text-white hover:bg-blue-700 disabled:opacity-50 dark:bg-blue-400 dark:text-gray-900 dark:hover:bg-blue-300" [disabled]="reorder.saving" (click)="reorder.onSave()">
                        {{ reorder.saving ? '저장 중…' : '완료' }}
                      </button>
                    </span>
                  } @else {
                    <button type="button" class="flex items-center rounded p-0.5 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="순서 변경" title="순서 변경" (click)="reorder.onStart()">
                      <mat-icon class="!h-4 !w-4 !text-base">swap_vert</mat-icon>
                    </button>
                  }
                </li>
              } @else {
                <li [class]="navHeadingClass">{{ entry.heading }}</li>
              }
            }
            @if (entry.reorder?.editing) {
              <!-- 순서 편집 모드: 항목 클릭(이동·action)은 막고 드래그 핸들과 위/아래 버튼만 둔다. 드래그가 어려운 모바일·키보드는 버튼으로 옮긴다. -->
              @let reorder = entry.reorder!;
              <!-- 드래그 코드(cdk)를 편집을 시작할 때만 불러온다. -->
              @defer (on immediate) {
                <li sbe-shell-nav-reorder-list [items]="entry.items" [reorder]="reorder"></li>
              } @placeholder {
                <li class="h-9"></li>
              }
            } @else {
              @for (item of entry.items; track item.label) {
                <ng-container
                  *ngTemplateOutlet="
                    navItemTpl;
                    context: { item, linkClass: entry.heading ? navLinkIndentedClass : navLinkClass }
                  "
                />
              }
            }
          }
        } @else {
          <ng-container *ngTemplateOutlet="navItemTpl; context: { item: entry, linkClass: navLinkClass }" />
        }
      }
    </ul>
  </nav>
  <ng-content select="[shellSidenavContent]" />
</ng-template>

<!--
  햄버거(header='none'+사이드바일 때만)와 다크모드 토글을 담는 콘텐츠 영역 상단 스트립.
  일반 흐름(sticky 아님)으로 페이지 자체의 상단 버튼 행과 겹치지 않게 한다. 헤더·컨텐츠·
  푸터와 같은 containerMaxWidthClass+containerPaddingClass를 써서, 다크모드 토글도 같은
  가장자리에 놓이게 한다(뒤이어 오는 <main>과 정확히 같은 폭·패딩) — 셸 소비처 대부분이
  <router-outlet>을 패딩 div로 감싸지 않는다.
-->
<!--
  noPaddingTop(기본 false): 이 스트립 바로 위에 이미 자기 여백/보더가 있는 헤더가 붙어
  나오는 소비처(현재 header='tabs' 분기)용 — 공유 기본값(pt-2)을 그대로 두고 그 소비처만
  0으로 넘긴다(2026-09-21, "상단 여백을 아예 없애달라"는 지적 — header='tabs'가 example-web
  전용이라 이 컨텍스트 하나만 늘어도 다른 분기/앱에 영향 없음).
-->
<ng-template #contentStrip let-noPaddingTop="noPaddingTop">
  @if (showContentStrip()) {
    <div
      class="flex w-full items-center gap-2"
      [class]="(hasSidenav() ? 'mr-auto' : 'mx-auto') + ' ' + containerMaxWidthClass() + ' ' + containerPaddingClass + (noPaddingTop ? '' : ' pt-2')"
    >
      @if (hamburgerInStrip()) {
        <button
          mat-icon-button
          type="button"
          [attr.aria-label]="sidenavState.opened() ? '메뉴 닫기' : '메뉴 열기'"
          [attr.aria-expanded]="sidenavState.opened()"
          (click)="sidenavState.toggle()"
        >
          <mat-icon>menu</mat-icon>
        </button>
      }
      <span class="flex-1"></span>
      @if (showThemeToggleInStrip()) {
        <sbe-theme-toggle />
      }
    </div>
  }
</ng-template>

<!--
  toolbar 안쪽(투영 슬롯 포함)도 sidenavBody와 같은 이유로 여기 한 번만 선언한다 — 사이드바
  유무에 따라 컨테이너로 감싸는 wrapper만 바뀌고, 안쪽 콘텐츠(햄버거·브랜드·슬롯)는
  *ngTemplateOutlet으로 참조해 하나만 유지한다.
-->
<ng-template #toolbarInner>
  @if (hasSidenav()) {
    <button
      mat-icon-button
      type="button"
      [attr.aria-label]="sidenavState.opened() ? '메뉴 닫기' : '메뉴 열기'"
      [attr.aria-expanded]="sidenavState.opened()"
      (click)="sidenavState.toggle()"
    >
      <mat-icon>menu</mat-icon>
    </button>
  }
  @if (brandInHeader()) {
    <sbe-shell-brand
      [logo]="resolvedLogo()"
      [logoDark]="resolvedLogoDark()"
      [logoAltText]="resolvedLogoAlt()"
      [label]="resolvedLabel()"
      [homeRoute]="homeRoute()"
    />
  }
  <ng-content select="[shellToolbarStart]" />
  <ng-content select="[shellToolbarContext]" />
  <span class="flex-1"></span>
  <!--
    헤더 우측 클러스터의 첫 항목 — 알림 종·프로필 메뉴 등 투영 슬롯(Actions/End)보다
    항상 앞에 온다(사용자 지시, 2026-08-14). 슬롯 투영이 아니라 셸이 직접 그리므로
    앱마다 app.html에 따로 추가할 필요가 없다 — showThemeToggle 옵트인만 켜면 전 앱에
    자동 적용된다.
  -->
  @if (showThemeToggleInToolbar()) {
    <sbe-theme-toggle />
  }
  <ng-content select="[shellToolbarActions]" />
  <ng-content select="[shellToolbarEnd]" />
</ng-template>

<!--
  이 템플릿은 hasSidenav()가 true일 때만 쓴다(toolbar/none 두 분기 공통) — mat-sidenav가
  같은 뷰 안에서 컨테이너 바로 아래 인스턴스화되므로 MatDrawerContainer의 ContentChildren
  쿼리가 계속 매칭된다(자식 컴포넌트로 빼면 이 쿼리가 못 찾는다 — Material 소스로 확인).
  mat-sidenav-container는 의도적으로 높이를 강제하지 않는다(autosize) — 페이지 전체가
  자연스럽게 스크롤되고, sidenav는 mat-sidenav-content의 콘텐츠 높이에 맞춰 늘어난다.
  이 autosize 로직은 JS로 콘텐츠 높이를 측정해 mat-sidenav-content에 인라인 height를
  고정해버려서(실측 확인), 바깥에서 flex-1을 줘도 안쪽에 위아래 중앙정렬용 여유 공간이
  생기지 않는다 — 그래서 사이드바가 없는 경우는 이 템플릿을 아예 타지 않는다(각 분기의
  hasSidenav() 조건 분기 참고).

  다만 이 autosize 특성 때문에 메인 콘텐츠가 짧으면(예: 목록이 비었을 때) mat-sidenav-container
  자체가 CDK autosize JS에 의해 그 짧은 콘텐츠 높이로(`overflow-y: hidden`과 함께) 고정돼버려서
  nav 항목이 화면 아래로 잘리고, 페이지 자체도 스크롤할 게 없어 잘린 항목을 볼 방법이 없어진다
  (2026-08-14, sbe-schedule nav 7개 중 2개가 안 보이는 버그로 실측 — 처음엔 mat-sidenav 자신에
  overflow-y-auto를 줬으나 효과 없었다, 부모 container가 overflow-hidden으로 이미 클리핑하고
  있어 자식에 뭘 줘도 소용없었다).
  container에 min-h-screen을 줘서 "짧은 콘텐츠에도 최소 한 화면 높이는 보장" — CSS는
  min-height가 height(inline이든 아니든)보다 항상 우선하므로, autosize가 240px처럼 짧게
  측정해도 실제 렌더 높이는 min-height로 클램프된다. autosize 자체(JS 측정 로직)는 그대로 둔다
  — 콘텐츠가 한 화면보다 길면 여전히 그 실제 높이로 늘어나 "페이지 전체가 자연스럽게
  스크롤"되는 원래 의도가 유지된다.

  이 템플릿은 'toolbar'/'none' 두 분기가 공유한다(위 분기 참고) — 'toolbar'는 이 위에
  mat-toolbar가 따로 있고, 'none'은 없다. 그래서 여기 min-height는 고정 100vh 대신
  `--shell-sidenav-chrome`(기본 0) 만큼 뺀 값을 쓴다 — toolbar가 있는 분기는 그 div에서
  이 변수를 toolbar 높이로 설정해 "toolbar+container 합이 정확히 한 화면"이 되게 하고
  (그래야 짧은 콘텐츠에서 toolbar 높이만큼 항상 남는 불필요한 스크롤이 없어진다, Gemini
  리뷰 지적), toolbar가 없는 'none' 분기는 변수를 안 건드려 기존 그대로 100vh를 보장한다
  — 두 분기가 같은 템플릿을 공유해도 서로 다른 케이스로 회귀시키지 않는다.
-->
<ng-template #sidenav>
  <mat-sidenav-container autosize class="min-h-[calc(100vh_-_var(--shell-sidenav-chrome,0px))]">
    @if (hasSidenav()) {
      <mat-sidenav
        [mode]="sidenavState.mode()"
        [opened]="sidenavState.opened()"
        (openedChange)="sidenavState.onOpenedChange($event)"
        [class]="sidenavWidthClass"
        class="border-r border-gray-200 dark:border-gray-700"
      >
        <ng-container *ngTemplateOutlet="sidenavBody" />
      </mat-sidenav>
    }

    <mat-sidenav-content>
      <ng-container *ngTemplateOutlet="contentStrip" />
      <main class="flex flex-1 flex-col">
        <!--
          헤더·푸터와 같은 containerPaddingClass(좌우)를 써서 가장자리를 통일하고,
          contentPaddingYClass(상하)로 헤더 바로 아래·푸터 바로 위에 콘텐츠가 붙어 보이지
          않게 한다(2026-08-28 — contentPaddingYClass 필드 주석 참고, contentStrip/tabs
          탭바에는 안 붙는 이유도 거기 있다). 라우트 컴포넌트 대부분은 지금까지 main에 패딩이
          없다는 가정 자체가 없었다(대부분 자체 패딩도 없이 그냥 화면 끝까지 붙어
          있었다). 페이지가 이미 자체 패딩을 갖고 있어 이중으로 겹치면 그 페이지에서
          [contentClass]로 대체하거나 자체 패딩을 제거해 맞춘다.
        -->
        <div
          class="mr-auto flex w-full flex-1 flex-col"
          [class]="containerMaxWidthClass() + ' ' + containerPaddingClass + ' ' + contentPaddingYClass"
        >
          <ng-container *ngTemplateOutlet="body" />
        </div>
      </main>
    </mat-sidenav-content>
  </mat-sidenav-container>
</ng-template>

<!--
  footerVariant 분기(compact/full)는 toolbar/tabs/none 세 분기가 전부 같은 조건을 쓴다 —
  하나로 모아 세 곳에 복붙되지 않게 한다(code-review 지적). tabs 분기만 mt-auto가 없다
  (mat-tab-nav-panel이 이미 flex-1 flex-col이라 다른 방식으로 하단에 붙는다) — 그 차이를
  context 파라미터로 남겨 기존 동작을 그대로 보존한다.
-->
<ng-template #footer let-mtAuto="mtAuto">
  @if (shouldRenderFooter()) {
    @if (resolvedFooterVariant() === 'compact') {
      <app-footer-compact [class.mt-auto]="mtAuto" />
    } @else {
      <app-footer [class.mt-auto]="mtAuto" />
    }
  }
</ng-template>

<!--
  shellToolbarStart/Context/Actions/End, shellBanner 슬롯은 header="toolbar" 분기에만
  존재한다 — header="tabs"|"none"으로 이 슬롯에 콘텐츠를 투영하면 아무 에러 없이 조용히
  버려진다(해당 분기가 렌더되지 않아 매칭되는 ng-content 자체가 없음). tabs/none 모드는
  toolbar 크롬이 없는 게 의도이므로 이 슬롯들을 지원하지 않는다. shellSidenavHeader는
  위 #sidenavBody에서 공유되므로 toolbar/none 두 분기 모두에서 지원된다.
-->
@switch (header()) {
  @case ('toolbar') {
    <!--
      footer는 mt-auto로 콘텐츠가 뷰포트보다 짧을 때만 하단에 붙고, 길면 콘텐츠 뒤로 흘러
      내려간다 — 'none' 분기의 footer와 동일한 sticky-footer 관례.

      --shell-sidenav-chrome: mat-toolbar 기본 높이(데스크톱 64px) — #sidenav 템플릿이
      이만큼 뺀 min-height를 쓰게 해서 toolbar+container 합이 한 화면을 넘지 않게 한다
      (위 #sidenav 주석 참고). 모바일(56px)보다 살짝 크게 잡아 안전 쪽으로 치우쳤다 —
      최악의 경우도 기존(항상 64px 초과 스크롤)보다 훨씬 적은 8px 여유 스크롤에 그친다.
    -->
    <div class="flex min-h-screen flex-col" style="--shell-sidenav-chrome: 4rem">
      <!--
        헤더는 사이드바 유무와 무관하게 항상 풀블리드다(2026-08-14) — toolbarClass가 폭
        제한(max-w-7xl) 없이 배경/보더만 화면 끝까지 이어지게 하고, 좌우 패딩은
        !px-4 sm:!px-6 lg:!px-8(전부 important)로 직접 준다 — 자세한 이유는
        app-shell.component.ts의 toolbarClass 주석 참고(일반 px-4를 !px-0과 같이 쓰면
        important끼리 충돌해 패딩이 0으로 죽는 회귀가 있었다). 사이드바가 있을 때(왼쪽
        시야각을 이미 사이드바가 쓰는 경우)나 없을 때(로그인 전 랜딩 등)나 동일해서,
        로그인 전후로 헤더가 센터 컨테이너 ↔ 풀블리드로 움직이는 것처럼 보이던 문제가
        없어졌다.
      -->
      <mat-toolbar [class]="toolbarClass">
        <ng-container *ngTemplateOutlet="toolbarInner" />
      </mat-toolbar>
      <ng-content select="[shellBanner]" />
      @if (hasSidenav()) {
        <ng-container *ngTemplateOutlet="sidenav" />
      } @else {
        <!--
          사이드바가 없으면 mat-sidenav-container(#sidenav)를 거치지 않고 main을 이 flex-col의
          직접 자식으로 둔다 — mat-sidenav-container는 autosize 모드에서 콘텐츠 높이를
          JS로 측정해 mat-sidenav-content에 인라인 height를 강제한다(실측: getComputedStyle
          height가 고정 px로 박혀 있음), 그래서 그 안의 main에 flex-1을 줘도 부모가 이미
          content-fit 높이로 고정돼 있어 위아래 중앙정렬에 쓸 여유 공간이 생기지 않는다.
          사이드바 자체가 없는 페이지(로그인 등)는 애초에 mat-sidenav-container가 관리할
          drawer가 없으므로, 'none' 분기의 사이드바 없는 경우와 동일하게 완전히 건너뛴다.
        -->
        <ng-container *ngTemplateOutlet="contentStrip" />
        <main class="flex flex-1 flex-col">
          <div
            class="mx-auto flex w-full flex-1 flex-col"
            [class]="containerMaxWidthClass() + ' ' + containerPaddingClass + ' ' + contentPaddingYClass"
          >
            <ng-container *ngTemplateOutlet="body" />
          </div>
        </main>
      }
      <ng-container *ngTemplateOutlet="footer; context: { mtAuto: true }" />
    </div>
  }
  @case ('tabs') {
    <!--
      tabs 헤더는 example-web 전용이라 Material tabs(약 34KB)를 초기 번들에서 뺀다 — `@defer`가
      `MatTabNav`·`MatTabLink`·`MatTabNavPanel`을 별도 청크로 지연 로딩한다. 이 세 지시자는 이 블록 안에서만
      쓴다(블록 밖에서 쓰면 지연되지 않는다).
    -->
    @defer (on immediate) {
      <div class="flex min-h-screen flex-col">
        <header class="border-b border-gray-200 dark:border-gray-700">
          <div
            class="mx-auto flex w-full flex-row flex-wrap items-center gap-x-6 gap-y-1"
            [class]="containerMaxWidthClass() + ' ' + containerPaddingClass"
          >
            @if (brandInHeader()) {
              <h1 class="m-0 shrink-0">
                <sbe-shell-brand
                  [logo]="resolvedLogo()"
                  [logoDark]="resolvedLogoDark()"
                  [logoAltText]="resolvedLogoAlt()"
                  [label]="resolvedLabel()"
                  [homeRoute]="homeRoute()"
                  logoClass="h-6 w-auto"
                  labelClass="text-xl font-semibold"
                />
              </h1>
            }
            <nav mat-tab-nav-bar [tabPanel]="tabPanel" aria-label="Primary">
              @for (tab of tabs(); track tab.path) {
                <a
                  mat-tab-link
                  [routerLink]="tab.path"
                  routerLinkActive
                  #rla="routerLinkActive"
                  [routerLinkActiveOptions]="{ exact: true }"
                  [active]="rla.isActive"
                >
                  {{ tab.label }}
                </a>
              }
            </nav>
          </div>
        </header>
        <mat-tab-nav-panel #tabPanel class="flex flex-1 flex-col">
          <ng-container *ngTemplateOutlet="contentStrip; context: { noPaddingTop: true }" />
          <main class="flex flex-1 flex-col">
            <!--
              나머지 두 분기(toolbar/none)와 달리 여기만 공유 contentPaddingYClass 대신
              상단 패딩을 0으로 직접 쓴다 — header가 이미 자기 border-b로 시각적 구분을
              만들어주는데, 공유 상수의 반응형 pt-4~pt-8까지 더하면 header와 본문 사이가
              과하게 벌어져 보였다(2026-09-21, 사용자 지적 — 처음엔 pt-2로 줄였다가 "아예
              없애달라"는 후속 요청으로 0까지 내림). 하단 패딩(footer 앞 여백)은 그대로
              공유값과 맞춘다. contentPaddingYClass 상수 자체는 건드리지 않는다 —
              toolbar/none 두 분기를 쓰는 다른 17개 앱에 영향 없이 이 분기(현재 example-web
              전용)만 조정한다.
            -->
            <div
              class="mx-auto flex w-full flex-1 flex-col pb-4 sm:pb-6 lg:pb-8"
              [class]="containerMaxWidthClass() + ' ' + containerPaddingClass"
            >
              <ng-container *ngTemplateOutlet="body" />
            </div>
          </main>
        </mat-tab-nav-panel>
        <ng-container *ngTemplateOutlet="footer; context: { mtAuto: false }" />
      </div>
  
    }
  }
  @case ('none') {
    <div class="flex min-h-screen flex-col">
      @if (hasSidenav()) {
        <ng-container *ngTemplateOutlet="sidenav" />
      } @else {
        <ng-container *ngTemplateOutlet="contentStrip" />
        <main class="flex flex-1 flex-col">
          <div
            class="mx-auto flex w-full flex-1 flex-col"
            [class]="containerMaxWidthClass() + ' ' + containerPaddingClass + ' ' + contentPaddingYClass"
          >
            <ng-container *ngTemplateOutlet="body" />
          </div>
        </main>
      }
      <ng-container *ngTemplateOutlet="footer; context: { mtAuto: true }" />
    </div>
  }
}
